/**
 * Custom hooks for Suggestion Chat
 * Handles chat messages on document suggestions
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiGet, apiPost, apiPut, apiDelete } from '@/lib/api-client';

// ============================================================================
// Types
// ============================================================================

export interface SuggestionChatMessage {
  suggestion_chat_id: string;
  document_user_suggestion_id: string;
  company_id: string;
  user_id: string | null;
  external_user_email: string | null;
  message: string;
  attribute_metadata: Record<string, unknown> | null;
  created_at: string;
  deleted_at: string | null;
}

export interface CreateChatMessageRequest {
  message: string;
  external_user_email?: string;
}

export interface UpdateChatMessageRequest {
  message: string;
}

// ============================================================================
// Query Keys
// ============================================================================

export const CHAT_QUERY_KEYS = {
  all: ['suggestion-chat'] as const,
  bySuggestion: (suggestionId: string) =>
    [...CHAT_QUERY_KEYS.all, suggestionId] as const,
};

// ============================================================================
// Hooks
// ============================================================================

/**
 * Fetch all chat messages for a suggestion
 */
export function useSuggestionChat(suggestionId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: CHAT_QUERY_KEYS.bySuggestion(suggestionId || ''),
    queryFn: async (): Promise<SuggestionChatMessage[]> => {
      return await apiGet<SuggestionChatMessage[]>(
        `/suggestions/${suggestionId}/chat`
      );
    },
    enabled: enabled && !!suggestionId,
    // Refresh every 30 seconds for near real-time updates
    refetchInterval: 30000,
  });
}

/**
 * Send a chat message to a suggestion
 */
export function useSendChatMessage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      suggestionId,
      message,
      externalUserEmail,
    }: {
      suggestionId: string;
      message: string;
      externalUserEmail?: string;
    }): Promise<SuggestionChatMessage> => {
      const request: CreateChatMessageRequest = {
        message,
        external_user_email: externalUserEmail,
      };
      return await apiPost<SuggestionChatMessage>(
        `/suggestions/${suggestionId}/chat`,
        request
      );
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: CHAT_QUERY_KEYS.bySuggestion(variables.suggestionId),
      });
    },
  });
}

/**
 * Update a chat message
 */
export function useUpdateChatMessage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      suggestionId,
      messageId,
      message,
    }: {
      suggestionId: string;
      messageId: string;
      message: string;
    }): Promise<SuggestionChatMessage> => {
      const request: UpdateChatMessageRequest = { message };
      return await apiPut<SuggestionChatMessage>(
        `/suggestions/${suggestionId}/chat/${messageId}`,
        request
      );
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: CHAT_QUERY_KEYS.bySuggestion(variables.suggestionId),
      });
    },
  });
}

/**
 * Delete a chat message
 */
export function useDeleteChatMessage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      suggestionId,
      messageId,
    }: {
      suggestionId: string;
      messageId: string;
    }): Promise<void> => {
      await apiDelete(`/suggestions/${suggestionId}/chat/${messageId}`);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: CHAT_QUERY_KEYS.bySuggestion(variables.suggestionId),
      });
    },
  });
}
